/**
 * Визард заказа профрисков (canon service-prof-risks, целевое состояние — centrika-docs!25).
 * Шаги: Организация → Анкета → Должности → Проверка, затем экран «Заказ отправлен». Прогресс-бар
 * (Stepper, «Шаг N из 4»), кнопки Назад/Далее, автосохранение черновика (debounced PATCH),
 * повторный прогон проверок перед подачей/выгрузкой (§3f). Для submitted-заказа
 * (anketa.canEdit=false) визард открывается read-only (просмотр анкеты).
 *
 * Шаг «Организация» (требование «Шаг 1. Организация»): контактное лицо — СОТРУДНИК организации,
 * поле «ФИО» — общий комбобокс `EmployeeSelect` (common.md, MR !22) с опцией создания сотрудника;
 * выбор подставляет телефон и e-mail, которые остаются редактируемыми. Отдельного действия
 * «Подставить из контактов» больше нет.
 *
 * Шаг «Анкета» (требование «Шаг 2. Анкета»): блок «Что уже есть на предприятии» (5 вопросов
 * «Да»/«Нет») + блок «Разработанные инструкции по охране труда» (да/нет → способ передачи →
 * файлы либо ручной список). Блока «Подтверждение анкеты предприятия» больше нет.
 *
 * Ошибки незаполненных элементов показываются ПО ПОПЫТКЕ перехода (требование: «При попытке
 * перейти к следующему шагу без ответа под соответствующим вопросом отображается ошибка»),
 * поэтому кнопка «Далее» активна всегда, а сам переход блокируется её обработчиком.
 */
import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Download } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  companyApi,
  ordersApi,
  profriskApi,
  ApiError,
  type CompanyProfile,
  type Employee,
  type OrderDetail,
  type ProfriskAnketa,
  type ProfriskChecklist,
  type ProfriskContact,
  type ProfriskInstruction,
  type ProfriskInstructionFile,
  type ProfriskInstructionsDeveloped,
  type ProfriskInstructionsMethod,
  type ProfriskPosition,
  type UpdateCompanyProfileInput,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { formatPhone } from '@/lib/sout'
import {
  CHECKLIST_ITEMS,
  PROFRISK_CONTACT_ERRORS,
  PROFRISK_CONTACT_FORMAT_ERRORS,
  PROFRISK_INSTRUCTIONS_ERRORS,
  positionsCountLabel,
  validateProfriskAnketa,
} from '@/lib/profrisk'
import { exportProfriskAnketaToExcel } from '@/lib/excel'
import {
  PROFRISK_AUTOSAVE_HINT,
  PROFRISK_CHECKLIST,
  PROFRISK_CHECKLIST_ERROR,
  PROFRISK_CHECKLIST_HINT,
  PROFRISK_CHECKLIST_TITLE,
  PROFRISK_CONTACT_PLACEHOLDERS,
  PROFRISK_HINTS,
  PROFRISK_IMPORT_DESCRIPTION,
  PROFRISK_IMPORT_TITLE,
  PROFRISK_INSTRUCTIONS_TITLE,
  PROFRISK_METHOD_CARDS,
  PROFRISK_NO,
  PROFRISK_ORG_SUBMITTED_HINT,
} from './profrisk-copy'
import { useDebounced } from '@/sout/use-debounced'
import { InstructionsBlock, YesNoGroup, type InstructionsBlockPatch } from './instructions-block'
import { PositionsStep } from './positions-step'
import { OrderImportDialog } from '@/orders/order-import-dialog'
import { CompanyRequisitesStep } from '@/company/requisites-step'
import { AddToStructureModal } from '@/company/add-to-structure-modal'
import { EmployeeSelect, type EmployeeSelectOption } from '@/company/employee-select'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import { Stepper } from '@/design-system/components/data/stepper'

const STEPS = ['Организация', 'Анкета', 'Должности', 'Проверка']

// --- Шаг: Организация (реквизиты + контактное лицо) ---

/**
 * Ошибки контактного лица по ПОЛЯМ формы (ФИО / Телефон / E-mail). В слот `fullName` попадают
 * и ошибки поля «ФИО» из 422 (`contact.personId` — PATCH, `contact.fullName` — submit).
 */
interface ContactErrors {
  fullName?: string
  phone?: string
  email?: string
}

function ContactEditor({
  contact,
  employees,
  errors,
  readOnly,
  onSelectEmployee,
  onFieldChange,
  onFieldCommit,
  onAddNew,
}: {
  contact: ProfriskContact
  employees: Employee[]
  errors: ContactErrors
  readOnly: boolean
  onSelectEmployee: (employee: Employee) => void
  onFieldChange: (patch: Partial<ProfriskContact>) => void
  /** Коммит правки в анкету/профиль (blur) — см. комментарий про 422 в `ProfriskWizard`. */
  onFieldCommit: () => void
  onAddNew: () => void
}) {
  const personId = (contact.personId ?? '').trim()
  // Выбранного сотрудника может не быть в activeOnly-списке (деактивирован/удалён) — тогда
  // показываем замороженное ФИО снимка анкеты, чтобы текущий выбор не «пропадал».
  const options: EmployeeSelectOption[] = employees.map((e) => ({
    value: e.id,
    label: e.fullName,
    fullName: e.fullName,
  }))

  return (
    <Card header="Контактное лицо">
      {/* «Строка 1: на одной строке расположены ФИО, Телефон, E-mail» (centrika-docs!25,
          ишью #2). До брейкпойнта `nw` (880px) поля переносятся в колонку — горизонтальной
          прокрутки блок не создаёт. Подписи, подсказки и ошибки остаются внутри своих `Field`. */}
      <div className="grid grid-cols-1 gap-3 nw:grid-cols-3">
        <Field label="ФИО" required={!readOnly} error={errors.fullName} className="mb-0">
          {(props) => (
            <EmployeeSelect
              value={personId}
              options={options}
              valueLabel={contact.fullName}
              aria-label="ФИО"
              aria-invalid={props['aria-invalid']}
              aria-describedby={props['aria-describedby']}
              aria-required={props['aria-required']}
              id={props.id}
              invalid={Boolean(errors.fullName)}
              disabled={readOnly}
              onAddNew={readOnly ? undefined : onAddNew}
              onSelect={(v) => {
                const emp = employees.find((e) => e.id === v)
                if (emp) onSelectEmployee(emp)
              }}
            />
          )}
        </Field>
        <Field
          label="Телефон"
          required={!readOnly}
          hint={PROFRISK_HINTS.contactPhone}
          error={errors.phone}
          className="mb-0"
        >
          {(props) => (
            <Input
              {...props}
              value={contact.phone}
              invalid={Boolean(errors.phone)}
              disabled={readOnly}
              onChange={(e) => onFieldChange({ phone: e.target.value })}
              onBlur={onFieldCommit}
              placeholder={PROFRISK_CONTACT_PLACEHOLDERS.phone}
            />
          )}
        </Field>
        <Field
          label="E-mail"
          required={!readOnly}
          hint={PROFRISK_HINTS.contactEmail}
          error={errors.email}
          className="mb-0"
        >
          {(props) => (
            <Input
              {...props}
              value={contact.email}
              invalid={Boolean(errors.email)}
              disabled={readOnly}
              onChange={(e) => onFieldChange({ email: e.target.value })}
              onBlur={onFieldCommit}
              placeholder={PROFRISK_CONTACT_PLACEHOLDERS.email}
            />
          )}
        </Field>
      </div>
    </Card>
  )
}

// --- Шаг: Анкета, блок «Что уже есть на предприятии» ---

function ChecklistEditor({
  checklist,
  readOnly,
  showErrors,
  onChange,
}: {
  checklist: ProfriskChecklist
  readOnly: boolean
  showErrors: boolean
  onChange: (next: ProfriskChecklist) => void
}) {
  return (
    <Card header={PROFRISK_CHECKLIST_TITLE}>
      <p className="mb-4 text-13 text-txt-secondary">{PROFRISK_CHECKLIST_HINT}</p>
      <ul className="flex flex-col gap-3">
        {CHECKLIST_ITEMS.map((item) => {
          const answered = checklist[item.key] === 'yes' || checklist[item.key] === 'no'
          const invalid = showErrors && !answered
          const errorId = `profrisk-checklist-${item.key}-error`
          return (
            <li key={item.key} className="border-b border-line pb-3 last:border-b-0 last:pb-0">
              <div className="flex flex-col gap-2 nw:flex-row nw:items-center nw:justify-between">
                <span className="text-14 text-txt">{item.label}</span>
                <YesNoGroup
                  label={item.label}
                  value={checklist[item.key]}
                  disabled={readOnly}
                  describedBy={invalid ? errorId : undefined}
                  onChange={(v) => onChange({ ...checklist, [item.key]: v })}
                />
              </div>
              {invalid ? (
                <p id={errorId} role="alert" className="mt-1.5 text-12 text-red">
                  {PROFRISK_CHECKLIST_ERROR}
                </p>
              ) : null}
            </li>
          )
        })}
      </ul>
    </Card>
  )
}

// --- Визард ---

export interface ProfriskWizardProps {
  order: OrderDetail
  anketa: ProfriskAnketa
  /** Вызывается после успешной отправки заказа (напр. перечитать контекст кабинета — centrika-docs!98). */
  onSubmitted?: () => void
}

export function ProfriskWizard({ order, anketa, onSubmitted }: ProfriskWizardProps) {
  const navigate = useNavigate()
  const readOnly = !anketa.canEdit

  const [step, setStep] = useState(0)
  // Реквизиты организации (§8.2, design D1): редактируемый снимок из анкеты — правится
  // клиентом без модерации через PATCH /orders/{id}/requisites (общий паттерн p2-company-profile).
  const [organization, setOrganization] = useState<CompanyProfile>(anketa.organization)
  const [contact, setContact] = useState<ProfriskContact>(anketa.contact)
  const [contactErrors, setContactErrors] = useState<ContactErrors>({})
  const [checklist, setChecklist] = useState<ProfriskChecklist>(anketa.checklist)
  const [developed, setDeveloped] = useState<ProfriskInstructionsDeveloped>(
    anketa.instructionsDeveloped,
  )
  const [method, setMethod] = useState<ProfriskInstructionsMethod>(anketa.instructionsMethod)
  const [instructions, setInstructions] = useState<ProfriskInstruction[]>(anketa.instructions)
  const [files, setFiles] = useState<ProfriskInstructionFile[]>(anketa.instructionFiles)
  const [positions, setPositions] = useState<ProfriskPosition[]>(anketa.positions)
  const [employees, setEmployees] = useState<Employee[]>([])
  const [addEmployeeOpen, setAddEmployeeOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [submitted, setSubmitted] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  // Шаги, на которых пользователь уже пытался нажать «Далее» — только там показываются
  // ошибки незаполненных элементов (требование: ошибка «при попытке перейти»).
  const [attempted, setAttempted] = useState<boolean[]>(() => STEPS.map(() => false))
  // Загрузка «Анкеты и формы (РИСКИ)» (order-import-legacy-forms): диалог и итог последней загрузки.
  const [importOpen, setImportOpen] = useState(false)
  const [importNotice, setImportNotice] = useState<string | null>(null)

  const anketaSave = useDebounced()
  const contactSave = useDebounced()
  const positionSave = useDebounced()

  // Актуальный снимок анкеты-части-1 для дебаунс-сейва (PATCH шлёт полный снимок — порядок
  // схлопываний неважен). Контакт живёт отдельным ref: он уходит СВОИМ PATCH (см. saveContact).
  const stateRef = useRef({
    checklist,
    instructionsDeveloped: developed,
    instructionsMethod: method,
    instructions,
  })
  useEffect(() => {
    stateRef.current = {
      checklist,
      instructionsDeveloped: developed,
      instructionsMethod: method,
      instructions,
    }
  }, [checklist, developed, method, instructions])

  const contactRef = useRef(contact)
  useEffect(() => {
    contactRef.current = contact
  }, [contact])

  useEffect(() => {
    if (readOnly) return
    const controller = new AbortController()
    // activeOnly: поле контактного лица нового заказа не предлагает деактивированных
    // (та же конвенция, что у пикеров сотрудников заказа СОУТ).
    companyApi
      .listEmployees({ limit: 200, activeOnly: true }, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setEmployees(res.items)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [readOnly])

  async function runSave(fn: () => Promise<unknown>) {
    setSaving(true)
    setSaveError(null)
    try {
      await fn()
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось сохранить черновик'))
    } finally {
      setSaving(false)
    }
  }

  function scheduleAnketaSave() {
    if (readOnly) return
    anketaSave.schedule(() =>
      runSave(() => profriskApi.patchAnketa(order.id, { ...stateRef.current })),
    )
  }

  /**
   * Сохранение контактного лица — ОТДЕЛЬНЫМ PATCH и только по коммиту правки (выбор сотрудника,
   * blur телефона/e-mail), а не на каждый символ: сервер валидирует телефон/e-mail и отвечает 422
   * (design D3/риски), поэтому посимвольный автосейв показывал бы ошибку посреди набора. Тело с
   * ключом `contact` также включает серверную синхронизацию профиля сотрудника — лишний раз его
   * тревожить не нужно, поэтому чек-лист/инструкции уходят своим PATCH без `contact`.
   */
  function saveContact() {
    if (readOnly) return
    contactSave.schedule(async () => {
      setSaving(true)
      setSaveError(null)
      try {
        await profriskApi.patchAnketa(order.id, { contact: contactRef.current })
        setContactErrors({})
      } catch (err) {
        if (err instanceof ApiError && err.status === 422) {
          const next: ContactErrors = {}
          for (const d of err.details ?? []) {
            if (d.field === 'contact.phone') next.phone = d.message
            else if (d.field === 'contact.email') next.email = d.message
            else if (d.field === 'contact.personId' || d.field === 'contact.fullName') {
              next.fullName = d.message
            }
          }
          // Ошибка показывается у соответствующего поля, а не общим алертом черновика.
          if (Object.keys(next).length > 0) {
            setContactErrors(next)
            return
          }
        }
        setSaveError(getErrorMessage(err, 'Не удалось сохранить черновик'))
      } finally {
        setSaving(false)
      }
    })
  }

  function changeContactField(patch: Partial<ProfriskContact>) {
    setContact((prev) => {
      const next = { ...prev, ...patch }
      contactRef.current = next
      return next
    })
  }

  /** Выбор сотрудника подставляет его телефон и e-mail; поля остаются редактируемыми. */
  function selectContactEmployee(emp: Employee) {
    const next: ProfriskContact = {
      personId: emp.id,
      fullName: emp.fullName,
      phone: emp.phone ? formatPhone(emp.phone) : '',
      email: emp.email ?? '',
    }
    setContact(next)
    contactRef.current = next
    setContactErrors({})
    saveContact()
  }

  // Загрузка из файла пишет на сервере и должности, и анкету (контакт, «Да/Нет», инструкции). Перед
  // ней досылаем отложенные автосейвы — иначе они долетели бы ПОСЛЕ загрузки и затёрли загруженное
  // прежним состоянием мастера; после — перечитываем анкету целиком.
  async function importFile(file: File) {
    await Promise.all([anketaSave.flush(), contactSave.flush(), positionSave.flush()])
    return profriskApi.importFile(order.id, file)
  }
  async function onImported(added: number) {
    setImportNotice(`Из файла добавлено: ${positionsCountLabel(added)}.`)
    try {
      const fresh = await profriskApi.getAnketa(order.id)
      setContact(fresh.contact)
      contactRef.current = fresh.contact
      setContactErrors({})
      setChecklist(fresh.checklist)
      setDeveloped(fresh.instructionsDeveloped)
      setMethod(fresh.instructionsMethod)
      setInstructions(fresh.instructions)
      setFiles(fresh.instructionFiles)
      setPositions(fresh.positions)
      stateRef.current = {
        checklist: fresh.checklist,
        instructionsDeveloped: fresh.instructionsDeveloped,
        instructionsMethod: fresh.instructionsMethod,
        instructions: fresh.instructions,
      }
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось обновить анкету — обновите страницу'))
    }
  }

  function changeChecklist(next: ProfriskChecklist) {
    setChecklist(next)
    stateRef.current = { ...stateRef.current, checklist: next }
    scheduleAnketaSave()
  }

  function changeInstructionsBlock(patch: InstructionsBlockPatch) {
    if (patch.instructionsDeveloped !== undefined) setDeveloped(patch.instructionsDeveloped)
    if (patch.instructionsMethod !== undefined) setMethod(patch.instructionsMethod)
    if (patch.instructions !== undefined) setInstructions(patch.instructions)
    stateRef.current = { ...stateRef.current, ...patch }
    scheduleAnketaSave()
  }

  function editPosition(position: ProfriskPosition) {
    setPositions((prev) => prev.map((p) => (p.id === position.id ? position : p)))
    positionSave.schedule(() =>
      runSave(() =>
        profriskApi.updatePosition(order.id, position.id, {
          departmentName: position.departmentName,
          position: position.position,
          // positionId авторитетен на бэкенде; при свободном вводе он пуст, и берётся текст
          // должности (position-free-text).
          positionId: position.positionId,
          headcount: position.headcount,
          vehicleUse: position.vehicleUse,
          equipment: position.equipment,
        }),
      ),
    )
  }

  // --- Валидность шагов (блокировка перехода) ---
  const validation = validateProfriskAnketa({
    contact,
    checklist,
    instructionsDeveloped: developed,
    instructionsMethod: method,
    instructions,
    instructionFiles: files,
    positions,
  })

  const stepValid: boolean[] = [
    // Шаг «Организация»: ФИО, телефон и e-mail контактного лица обязательны (centrika-docs!27);
    // реквизиты организации по-прежнему не валидируются (снимок профиля).
    validation.contactComplete,
    validation.checklistComplete && validation.instructionsComplete,
    validation.hasPositions && validation.invalidPositionCount === 0,
    validation.ok,
  ]

  // Ошибки контактного лица: 422 сервера (формат телефона/e-mail по blur) + незаполненные
  // обязательные поля ПО ПОПЫТКЕ перехода — та же механика, что у шага «Анкета».
  const contactFieldErrors: ContactErrors = { ...contactErrors }
  if (!readOnly && attempted[0]) {
    for (const f of validation.contactMissing) {
      contactFieldErrors[f] ??= PROFRISK_CONTACT_ERRORS[f]
    }
    // Неверный формат телефона/e-mail тоже держит на шаге (ишью #3): показываем ту же
    // ошибку, что вернул бы сервер, даже если запрос ещё не уходил (правка без blur).
    for (const f of validation.contactInvalid) {
      contactFieldErrors[f] ??= PROFRISK_CONTACT_FORMAT_ERRORS[f]
    }
  }

  function markAttempted(i: number) {
    setAttempted((prev) => (prev[i] ? prev : prev.map((v, j) => (j === i ? true : v))))
  }

  function next() {
    if (step >= STEPS.length - 1) return
    if (!readOnly && !stepValid[step]) {
      markAttempted(step)
      return
    }
    setStep(step + 1)
  }
  function back() {
    if (step > 0) setStep(step - 1)
  }

  async function submit() {
    setSubmitError(null)
    setFieldErrors([])
    if (!validation.ok) {
      setAttempted(STEPS.map(() => true))
      setSubmitError('Проверьте заполнение: анкета и должности заполнены не полностью.')
      return
    }
    setSubmitting(true)
    try {
      // Дожидаемся долёта последних отложенных PATCH (анкета + контакт + должность), затем подаём.
      await anketaSave.flush()
      await contactSave.flush()
      await positionSave.flush()
      await ordersApi.submit(order.id)
      setSubmitted(true)
      onSubmitted?.()
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setSubmitError('Сервер отклонил подачу: проверьте поля.')
        setFieldErrors((err.details ?? []).map((d) => `${d.field}: ${d.message}`))
      } else {
        setSubmitError(getErrorMessage(err, 'Не удалось отправить заказ'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  function exportExcel() {
    setExportError(null)
    try {
      exportProfriskAnketaToExcel(
        {
          organization,
          contact,
          checklist,
          instructionsDeveloped: developed,
          instructionsMethod: method,
          instructions,
          instructionFiles: files,
          positions,
          canEdit: anketa.canEdit,
        },
        order.id,
      )
    } catch (err) {
      setExportError(getErrorMessage(err, 'Не удалось выгрузить'))
    }
  }

  if (submitted) {
    return (
      <Card>
        <div className="flex flex-col items-center px-5 py-16 text-center">
          <CheckCircle2 className="mb-4 size-12 stroke-current text-green" aria-hidden />
          <h2 className="text-20 font-semibold text-txt">Заказ отправлен</h2>
          <p className="mt-2 max-w-narrow text-txt-secondary">
            Анкета профрисков принята на проверку. Менеджер Центрики свяжется с вами; статус и ход
            работ видны в карточке заказа.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2.5">
            <Button onClick={() => navigate(`/orders/${order.id}`)}>Открыть заказ</Button>
            <Button variant="secondary" onClick={() => navigate('/profrisks')}>
              К дашборду «Профриски»
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div>
      <Stepper steps={STEPS} current={step} className="mb-3" />
      <div className="mb-4 flex items-center justify-between gap-3 text-12 text-txt-tertiary">
        <span>
          Шаг {step + 1} из {STEPS.length} · {PROFRISK_AUTOSAVE_HINT}
        </span>
        <span aria-live="polite">{saving ? 'Сохранение…' : saveError ? '' : 'Черновик сохранён'}</span>
      </div>

      {saveError ? (
        <Alert variant="danger" className="mb-4">
          {saveError}
        </Alert>
      ) : null}
      {readOnly ? (
        <Alert variant="info" title="Только просмотр" className="mb-4">
          Заказ уже отправлен — анкета доступна только для чтения.
        </Alert>
      ) : null}

      {/* Шаг 0 — Организация */}
      {step === 0 ? (
        <div className="flex flex-col gap-4">
          {!readOnly ? (
            <Alert variant="info" title="Что подготовить">
              <ul className="ml-4 list-disc">
                {PROFRISK_CHECKLIST.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          {/* Read-only компакт + «Изменить» (F2-11/FX-E) — общий паттерн заказов обучения/СОУТ. */}
          <CompanyRequisitesStep
            organization={organization}
            readOnly={readOnly}
            readOnlyHint={PROFRISK_ORG_SUBMITTED_HINT}
            hideManagerFields
            onDaDataLookup={companyApi.dadataLookup}
            onSave={async (input: UpdateCompanyProfileInput) => {
              await ordersApi.patchRequisites(order.id, input)
              const next: CompanyProfile = { ...organization, ...input }
              setOrganization(next)
              return next
            }}
          />
          <ContactEditor
            contact={contact}
            employees={employees}
            errors={contactFieldErrors}
            readOnly={readOnly}
            onSelectEmployee={selectContactEmployee}
            onFieldChange={changeContactField}
            onFieldCommit={saveContact}
            onAddNew={() => setAddEmployeeOpen(true)}
          />
        </div>
      ) : null}

      {/* Шаг 1 — Анкета */}
      {step === 1 ? (
        <div className="flex flex-col gap-4">
          <ChecklistEditor
            checklist={checklist}
            readOnly={readOnly}
            showErrors={!readOnly && attempted[1]}
            onChange={changeChecklist}
          />
          <InstructionsBlock
            orderId={order.id}
            developed={developed}
            method={method}
            instructions={instructions}
            files={files}
            readOnly={readOnly}
            errorField={!readOnly && attempted[1] ? validation.instructionsError : null}
            onChange={changeInstructionsBlock}
            onFilesChange={setFiles}
          />
        </div>
      ) : null}

      {/* Шаг 2 — Должности */}
      {step === 2 && importNotice ? (
        <Alert variant="info" className="mb-4">
          {importNotice} Проверьте должности и анкету на шагах «Организация» и «Анкета».
        </Alert>
      ) : null}
      {importOpen ? (
        <OrderImportDialog
          title={PROFRISK_IMPORT_TITLE}
          description={PROFRISK_IMPORT_DESCRIPTION}
          preview={(file) => profriskApi.importPreview(order.id, file)}
          importFile={(file) => importFile(file)}
          onImported={(result) => void onImported(result.added)}
          onClose={() => setImportOpen(false)}
        />
      ) : null}
      {step === 2 ? (
        <PositionsStep
          orderId={order.id}
          positions={positions}
          readOnly={readOnly}
          onPositionsChange={setPositions}
          onPositionEdit={editPosition}
          onFlushEdits={() => positionSave.flush()}
          onImportFile={readOnly ? undefined : () => setImportOpen(true)}
        />
      ) : null}

      {/* Шаг 3 — Проверка */}
      {step === 3 ? (
        <div className="flex flex-col gap-4">
          <Card header="Проверка перед отправкой">
            <ul className="flex flex-col gap-2">
              {/* Контактное лицо обязательно (centrika-docs!27) — сводка перечисляет его
                  наравне с остальными проверками подачи. */}
              <CheckRow ok={validation.contactComplete} label="Контактное лицо">
                {validation.contactComplete
                  ? contact.fullName
                  : 'Заполните ФИО, телефон и e-mail на шаге «Организация»'}
              </CheckRow>
              <CheckRow ok={validation.checklistComplete} label={PROFRISK_CHECKLIST_TITLE}>
                {validation.checklistComplete
                  ? 'Все вопросы отвечены'
                  : `Ответьте на все ${CHECKLIST_ITEMS.length} вопроса`}
              </CheckRow>
              <CheckRow ok={validation.instructionsComplete} label={PROFRISK_INSTRUCTIONS_TITLE}>
                {validation.instructionsError
                  ? PROFRISK_INSTRUCTIONS_ERRORS[validation.instructionsError]
                  : developed === 'no'
                    ? PROFRISK_NO
                    : method === 'files'
                      ? `${PROFRISK_METHOD_CARDS.files.title}: ${files.length} шт.`
                      : `${PROFRISK_METHOD_CARDS.manual.title}: ${
                          instructions.filter((i) => i.name.trim()).length
                        } шт.`}
              </CheckRow>
              <CheckRow
                ok={validation.hasPositions && validation.invalidPositionCount === 0}
                label="Должности"
              >
                {!validation.hasPositions
                  ? 'Добавьте хотя бы одну должность'
                  : validation.invalidPositionCount > 0
                    ? `Дозаполнить ${validation.invalidPositionCount} шт.`
                    : `${positions.length} должн. — все заполнены`}
              </CheckRow>
            </ul>

            {submitError ? (
              <Alert variant="danger" className="mt-4">
                {submitError}
              </Alert>
            ) : null}
            {fieldErrors.length > 0 ? (
              <ul className="ml-4 mt-2 list-disc text-13 text-red">
                {fieldErrors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            ) : null}
            {exportError ? (
              <Alert variant="danger" className="mt-4">
                {exportError}
              </Alert>
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2.5">
              {!readOnly ? (
                <Button disabled={submitting} onClick={() => void submit()}>
                  {submitting ? 'Отправка…' : 'Отправить'}
                </Button>
              ) : null}
              {/* Выгрузка не требует контактного лица (centrika-docs!27 говорит только о переходе
                  со шага и подаче): у заказов, отправленных до требования, контакт может быть
                  пуст, а поля уже read-only — иначе такой заказ нельзя было бы выгрузить вообще. */}
              <Button
                variant="secondary"
                disabled={!validation.canExport}
                onClick={exportExcel}
                leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
              >
                Выгрузить в Excel
              </Button>
            </div>
          </Card>
        </div>
      ) : null}

      {/* Навигация шагов */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <Button variant="secondary" disabled={step === 0} onClick={back}>
          Назад
        </Button>
        <div className="flex items-center gap-3">
          {!readOnly && step < STEPS.length - 1 && attempted[step] && !stepValid[step] ? (
            <span className="text-12 text-red">На шаге есть незаполненные поля</span>
          ) : null}
          {step < STEPS.length - 1 ? <Button onClick={next}>Далее</Button> : null}
        </div>
      </div>

      {/* «+ Добавить сотрудника» поля «ФИО» контактного лица: переиспользуем модалку «Добавить в
          структуру» (вкладка «Добавить сотрудника»), как шаги «Комиссия»/«Ответственный» СОУТ.
          По успеху созданный сотрудник становится значением поля, его телефон и e-mail
          подставляются; закрытие — самой модалкой. */}
      {addEmployeeOpen && !readOnly ? (
        <AddToStructureModal
          api={companyApi}
          initialTab="employee"
          onClose={() => setAddEmployeeOpen(false)}
          onSuccess={(info) => {
            if (info.tab === 'employee') {
              setEmployees((prev) =>
                prev.some((e) => e.id === info.employee.id) ? prev : [info.employee, ...prev],
              )
              selectContactEmployee(info.employee)
            }
            setAddEmployeeOpen(false)
          }}
        />
      ) : null}
    </div>
  )
}

function CheckRow({
  ok,
  label,
  children,
}: {
  ok: boolean
  label: string
  children: React.ReactNode
}) {
  return (
    <li className="flex items-center gap-2.5">
      <CheckCircle2
        className={
          ok
            ? 'size-4 shrink-0 stroke-current text-green'
            : 'size-4 shrink-0 stroke-current text-txt-tertiary'
        }
        aria-hidden
      />
      <span className="font-medium text-txt">{label}:</span>
      <span className={ok ? 'text-txt-secondary' : 'text-red'}>{children}</span>
    </li>
  )
}
